import { useState, useEffect } from 'react'
import clsx from 'clsx'
import axios from 'axios'
import { X } from 'lucide-react'
import { buildApiUrl } from '../config/api'
import { toast } from './Toast'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from './ui'
import { maskPhone, unmaskPhone, maskCPF, unmaskCPF, maskCEP, unmaskCEP } from '../utils/masks'
import { isValidTituloEleitor } from '../utils/titulo'
import { inputClass } from '../styles/forms'
import LeaderSelect from './LeaderSelect'

interface Channel { id: number; code: string; label: string }
interface Leader { id: number; name: string; nickname: string | null }

interface CreateVoterModalProps {
  open: boolean
  onClose: () => void
  onCreated: (voter: { id: number; name: string; phone: string }) => void
  defaultName?: string
  type?: 'contact' | 'voter'
}

export default function CreateVoterModal({ open, onClose, onCreated, defaultName, type = 'contact' }: CreateVoterModalProps) {
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  // Config de campos do formulário (Settings → Formulário de Eleitor, contexto interno)
  const [voterCfg, setVoterCfg] = useState<Record<string, { visible: boolean; required: boolean }>>({})
  const showF = (key: string) => voterCfg[key]?.visible ?? true
  const reqF = (key: string) => voterCfg[key]?.required ?? false
  const [cepLoading, setCepLoading] = useState(false)
  const [channels, setChannels] = useState<Channel[]>([])
  const [familyMode, setFamilyMode] = useState(false)
  const [familyMembers, setFamilyMembers] = useState<any[]>([])
  const [zones, setZones] = useState<{ id: number; nr_zona: number; nm_municipio: string }[]>([])
  const [sections, setSections] = useState<{ id: number; nr_secao: number; nm_local_votacao: string | null }[]>([])
  const [zonesLoaded, setZonesLoaded] = useState(false)
  const [familySections, setFamilySections] = useState<Record<number, { id: number; nr_secao: number; nm_local_votacao: string | null }[]>>({})
  const [otherCity, setOtherCity] = useState(false)
  const [electoralCities, setElectoralCities] = useState<{ id: number; name: string }[]>([])
  const [electoralCitySearch, setElectoralCitySearch] = useState('')
  const [electoralCityDropdownOpen, setElectoralCityDropdownOpen] = useState(false)
  const [selectedElectoralCity, setSelectedElectoralCity] = useState<{ id: number; name: string } | null>(null)

  const [form, setForm] = useState({
    name: '', nickname: '', phone: '', email: '', cpf: '', sus_number: '',
    birth_date: '', gender: '',
    voter_registration: '', tse_zone_id: '', tse_section_id: '',
    zip_code: '', street: '', number: '', neighborhood: '', complement: '',
    geo_city_id: '', geo_neighborhood_id: '', city_display: '',
    capture_channel_id: '', leader_id: '', notes: '',
  })

  useEffect(() => {
    if (open) {
      setForm(f => ({
        ...f, name: defaultName || '', nickname: '', phone: '', email: '', cpf: '', sus_number: '',
        birth_date: '', gender: '', voter_registration: '', tse_zone_id: '', tse_section_id: '',
        zip_code: '', street: '', number: '', neighborhood: '', complement: '',
        geo_city_id: '', geo_neighborhood_id: '', city_display: '',
        capture_channel_id: '', leader_id: '', notes: '',
      }))
      setErrors({}); setFamilyMode(false); setFamilyMembers([]); setFamilySections({})
      setOtherCity(false); setElectoralCities([]); setElectoralCitySearch(''); setSelectedElectoralCity(null)
      // Carregar config dos campos (contexto interno)
      axios.get(buildApiUrl('/v1/settings/voter-form'), { params: { context: 'internal' } })
        .then(r => {
          const map: Record<string, { visible: boolean; required: boolean }> = {}
          for (const f of r.data.data.fields) map[f.key] = { visible: f.visible, required: f.required }
          setVoterCfg(map)
        })
        .catch(() => setVoterCfg({}))
      if (channels.length === 0) {
        axios.get(buildApiUrl('/v1/voters/channels')).then(r => {
          setChannels(r.data.data)
          const manual = r.data.data.find((c: Channel) => c.code === 'MANUAL')
          if (manual) setForm(f => ({ ...f, capture_channel_id: String(manual.id) }))
        }).catch(() => {})
      } else {
        const manual = channels.find(c => c.code === 'MANUAL')
        if (manual) setForm(f => ({ ...f, capture_channel_id: String(manual.id) }))
      }
      setZones([]); setSections([]); setZonesLoaded(false)
    }
  }, [open, defaultName])


  function toggleOtherCity() {
    const next = !otherCity
    setOtherCity(next)
    setForm(f => ({ ...f, tse_zone_id: '', tse_section_id: '' }))
    setZones([]); setSections([])
    setSelectedElectoralCity(null); setElectoralCitySearch('')
    if (next && electoralCities.length === 0) {
      // Carregar todas as cidades do estado da config
      axios.get(buildApiUrl('/v1/political/config')).then(configRes => {
        const stateId = configRes.data.data?.config?.geo_state_id
        if (stateId) {
          axios.get(buildApiUrl(`/v1/geo/states/${stateId}/cities`)).then(r => {
            setElectoralCities(r.data.data.map((c: any) => ({ id: c.id, name: c.name })))
          }).catch(() => {})
        }
      }).catch(() => {})
    }
    if (!next && form.geo_city_id) {
      loadZonesForCity(form.geo_city_id)
    }
  }

  function selectElectoralCity(city: { id: number; name: string }) {
    setSelectedElectoralCity(city)
    setElectoralCitySearch('')
    setElectoralCityDropdownOpen(false)
    loadZonesForCity(String(city.id))
  }

  function loadZonesForCity(cityId: string) {
    setZones([]); setSections([]); setZonesLoaded(false)
    axios.get(buildApiUrl('/v1/elections/zones'), { params: { city_id: cityId } }).then(r => {
      setZones(r.data.data.map((z: any) => ({ id: z.id, nr_zona: z.nr_zona, nm_municipio: z.nm_municipio })))
      setZonesLoaded(true)
    }).catch(() => {})
  }

  function onFamilyZoneChange(memberIndex: number, zoneId: string) {
    updateFamilyMember(memberIndex, 'tse_zone_id', zoneId)
    updateFamilyMember(memberIndex, 'tse_section_id', '')
    setFamilySections(prev => ({ ...prev, [memberIndex]: [] }))
    if (zoneId) {
      axios.get(buildApiUrl(`/v1/elections/zones/${zoneId}/sections`)).then(r => {
        setFamilySections(prev => ({ ...prev, [memberIndex]: r.data.data.map((s: any) => ({ id: s.id, nr_secao: s.nr_secao, nm_local_votacao: s.nm_local_votacao })) }))
      }).catch(() => {})
    }
  }

  function onZoneChange(zoneId: string) {
    setForm(f => ({ ...f, tse_zone_id: zoneId, tse_section_id: '' }))
    setSections([])
    if (zoneId) {
      axios.get(buildApiUrl(`/v1/elections/zones/${zoneId}/sections`)).then(r => {
        setSections(r.data.data.map((s: any) => ({ id: s.id, nr_secao: s.nr_secao, nm_local_votacao: s.nm_local_votacao })))
      }).catch(() => {})
    }
  }

  async function handleCepLookup() {
    const cep = unmaskCEP(form.zip_code)
    if (cep.length < 8) return
    setCepLoading(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/geo/cep/${cep}`))
      const d = res.data.data
      const cityId = d.geo_city_id?.toString() || ''
      setForm(f => ({
        ...f, street: d.street || f.street, neighborhood: d.neighborhood || f.neighborhood,
        geo_city_id: cityId || f.geo_city_id,
        geo_neighborhood_id: d.geo_neighborhood_id?.toString() || f.geo_neighborhood_id,
        city_display: d.city_name && d.state_code ? `${d.city_name}/${d.state_code}` : f.city_display,
        tse_zone_id: '', tse_section_id: '',
      }))
      if (cityId) loadZonesForCity(cityId)
    } catch { /* */ } finally { setCepLoading(false) }
  }

  function addFamilyMember() { setFamilyMembers(m => [...m, { name: '', nickname: '', phone: '', cpf: '', email: '', voter_registration: '', tse_zone_id: '', tse_section_id: '' }]) }
  function removeFamilyMember(i: number) { setFamilyMembers(m => m.filter((_, idx) => idx !== i)) }
  function updateFamilyMember(i: number, key: string, value: string) { setFamilyMembers(m => m.map((mem, idx) => idx === i ? { ...mem, [key]: value } : mem)) }

  // Rola até o primeiro campo destacado com erro e foca
  function focusFirstError() {
    setTimeout(() => {
      const el = document.querySelector('.border-error-300, .border-error-400') as HTMLElement | null
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus?.() }
    }, 60)
  }

  async function handleSave() {
    if (saving) return
    // Checagens instantâneas no front; a obrigatoriedade conforme a config é validada no backend
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Informe o nome completo.'
    if (!form.phone.trim()) e.phone = 'Informe o telefone.'
    if (form.voter_registration && !isValidTituloEleitor(form.voter_registration)) e.voter_registration = 'Título de eleitor inválido — confira os números.'

    setErrors(e)
    if (Object.keys(e).length > 0) { toast('error', 'Verifique os campos destacados'); focusFirstError(); return }

    setSaving(true)
    try {
      const payload: any = {
        name: form.name, nickname: form.nickname || null,
        cpf: unmaskCPF(form.cpf) || null, sus_number: form.sus_number || null, phone: unmaskPhone(form.phone),
        email: form.email || null, birth_date: form.birth_date || null, gender: form.gender || null,
        voter_registration: form.voter_registration || null,
        tse_zone_id: form.tse_zone_id ? Number(form.tse_zone_id) : null,
        tse_section_id: form.tse_section_id ? Number(form.tse_section_id) : null,
        zip_code: unmaskCEP(form.zip_code) || null, street: form.street || null,
        number: form.number || null, neighborhood: form.neighborhood || null, complement: form.complement || null,
        geo_city_id: form.geo_city_id ? parseInt(form.geo_city_id) : null,
        geo_neighborhood_id: form.geo_neighborhood_id ? parseInt(form.geo_neighborhood_id) : null,
        capture_channel_id: form.capture_channel_id ? parseInt(form.capture_channel_id) : null,
        leader_id: form.leader_id ? parseInt(form.leader_id) : null,
        notes: form.notes || null,
        is_contact: type === 'contact', is_voter: type === 'voter',
      }

      let voterId: number
      if (familyMode && familyMembers.filter(m => m.name && m.phone).length > 0) {
        const allMembers = [
          { name: form.name, nickname: form.nickname || null, phone: unmaskPhone(form.phone), cpf: unmaskCPF(form.cpf) || null, email: form.email || null },
          ...familyMembers.filter(m => m.name && m.phone).map(m => ({
            name: m.name, nickname: m.nickname || null, phone: unmaskPhone(m.phone), cpf: unmaskCPF(m.cpf) || null, email: m.email || null,
            voter_registration: m.voter_registration || null,
            tse_zone_id: m.tse_zone_id ? Number(m.tse_zone_id) : null,
            tse_section_id: m.tse_section_id ? Number(m.tse_section_id) : null,
          })),
        ]
        const res = await axios.post(buildApiUrl('/v1/voters/family'), { ...payload, members: allMembers })
        voterId = res.data.data.family_group_id ? res.data.data.voters[0] : res.data.data.id
        toast('success', `${allMembers.length} contatos cadastrados`)
      } else {
        const res = await axios.post(buildApiUrl('/v1/voters'), payload)
        voterId = res.data.data.id
        toast('success', type === 'voter' ? 'Eleitor cadastrado' : 'Contato cadastrado')
      }

      onCreated({ id: voterId, name: form.name, phone: unmaskPhone(form.phone) })
      onClose()
    } catch (err: any) {
      const errData = err.response?.data
      if (errData?.errors) {
        const fieldErrors: Record<string, string> = {}
        Object.entries(errData.errors).forEach(([field, msgs]: [string, any]) => {
          fieldErrors[field] = Array.isArray(msgs) ? msgs[0] : msgs
        })
        setErrors(fieldErrors)
        toast('error', errData?.message || 'Verifique os campos destacados')
        focusFirstError()
      } else {
        toast('error', errData?.error || errData?.message || 'Erro ao cadastrar')
      }
    } finally { setSaving(false) }
  }

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>{type === 'voter' ? 'Novo Eleitor' : 'Novo Contato'}</ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          {/* Dados básicos — flui e preenche o espaço conforme os campos visíveis */}
          <div className="flex flex-wrap gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo <span className="text-error-500">*</span></label>
              <input value={form.name} onChange={e => { setForm({ ...form, name: e.target.value }); setErrors(er => ({ ...er, name: '' })) }} placeholder="Nome completo"
                className={clsx(inputClass, errors.name && 'border-error-300')} />
              {errors.name && <p className="mt-1 text-sm text-error-500">{errors.name}</p>}
            </div>

            <div className="flex-1 min-w-[calc(50%-0.5rem)]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone <span className="text-error-500">*</span></label>
              <input value={maskPhone(form.phone)} onChange={e => { setForm({ ...form, phone: e.target.value }); setErrors(er => ({ ...er, phone: '' })) }} placeholder="(00) 00000-0000"
                className={clsx(inputClass, errors.phone && 'border-error-300')} />
              {errors.phone && <p className="mt-1 text-sm text-error-500">{errors.phone}</p>}
            </div>

            {showF('email') && (
            <div className="flex-1 min-w-[calc(50%-0.5rem)]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">E-mail {reqF('email') && <span className="text-error-500">*</span>}</label>
              <input type="email" value={form.email} onChange={e => { setForm({ ...form, email: e.target.value }); setErrors(er => ({ ...er, email: '' })) }} placeholder="email@exemplo.com"
                className={clsx(inputClass, errors.email && 'border-error-300')} />
              {errors.email && <p className="mt-1 text-sm text-error-500">{errors.email}</p>}
            </div>
            )}

            {showF('cpf') && (
            <div className="flex-1 min-w-[calc(50%-0.5rem)]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF {reqF('cpf') && <span className="text-error-500">*</span>}</label>
              <input value={maskCPF(form.cpf)} onChange={e => { setForm({ ...form, cpf: e.target.value }); setErrors(er => ({ ...er, cpf: '' })) }} placeholder="000.000.000-00"
                className={clsx(inputClass, errors.cpf && 'border-error-300')} />
              {errors.cpf && <p className="mt-1 text-sm text-error-500">{errors.cpf}</p>}
            </div>
            )}

            {showF('birth_date') && (
            <div className="flex-1 min-w-[calc(50%-0.5rem)]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Data de nascimento {reqF('birth_date') && <span className="text-error-500">*</span>}</label>
              <input type="date" value={form.birth_date} onChange={e => { setForm({ ...form, birth_date: e.target.value }); setErrors(er => ({ ...er, birth_date: '' })) }} className={clsx(inputClass, errors.birth_date && 'border-error-300')} />
              {errors.birth_date && <p className="mt-1 text-sm text-error-500">{errors.birth_date}</p>}
            </div>
            )}

            {showF('gender') && (
            <div className="flex-1 min-w-[calc(50%-0.5rem)]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Gênero {reqF('gender') && <span className="text-error-500">*</span>}</label>
              <select value={form.gender} onChange={e => { setForm({ ...form, gender: e.target.value }); setErrors(er => ({ ...er, gender: '' })) }} className={clsx(inputClass, 'appearance-none', errors.gender && 'border-error-300')}>
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="O">Outro</option>
              </select>
              {errors.gender && <p className="mt-1 text-sm text-error-500">{errors.gender}</p>}
            </div>
            )}

            {showF('nickname') && (
            <div className="flex-1 min-w-[calc(50%-0.5rem)]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Apelido {reqF('nickname') && <span className="text-error-500">*</span>}</label>
              <input value={form.nickname} onChange={e => { setForm({ ...form, nickname: e.target.value }); setErrors(er => ({ ...er, nickname: '' })) }} placeholder="Como é conhecido" className={clsx(inputClass, errors.nickname && 'border-error-300')} />
              {errors.nickname && <p className="mt-1 text-sm text-error-500">{errors.nickname}</p>}
            </div>
            )}

            {showF('sus_number') && (
            <div className="flex-1 min-w-[calc(50%-0.5rem)]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Cartão SUS {reqF('sus_number') && <span className="text-error-500">*</span>}</label>
              <input value={form.sus_number} onChange={e => setForm({ ...form, sus_number: e.target.value.replace(/\D/g, '').slice(0, 15) })} placeholder="000 0000 0000 0000" className={inputClass} />
            </div>
            )}
          </div>

          {/* Liderança */}
          <LeaderSelect label="Liderança vinculada" value={form.leader_id} onChange={id => setForm({ ...form, leader_id: id })} dropdownZIndex={100003} />

          {showF('address') && (
          <div className="border-t border-gray-100 pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Endereço {reqF('address') && <span className="text-error-500">*</span>}</p>
            {errors.geo_city_id && <p className="mb-3 text-sm text-error-500">{errors.geo_city_id}</p>}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">CEP</label>
                <div className="relative">
                  <input value={form.zip_code} onChange={e => { setForm({ ...form, zip_code: maskCEP(e.target.value) }); setErrors(er => ({ ...er, geo_city_id: '' })) }} onBlur={handleCepLookup} placeholder="00000-000" className={clsx(inputClass, 'pr-10', errors.geo_city_id && 'border-error-300')} />
                  {cepLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-4 h-4 border-2 border-brand-200 border-t-brand-500 rounded-full animate-spin" /></div>}
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Rua</label>
                <input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Número</label>
                <input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Bairro</label>
                <input value={form.neighborhood} onChange={e => setForm({ ...form, neighborhood: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Complemento</label>
                <input value={form.complement} onChange={e => setForm({ ...form, complement: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cidade/UF</label>
                <input value={form.city_display} readOnly placeholder="Via CEP" className="h-11 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-sm text-gray-600 cursor-not-allowed" />
              </div>
            </div>
          </div>
          )}

          {/* Dados eleitorais */}
          {(showF('voter_registration') || showF('electoral_section')) && (
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-700">Dados eleitorais</p>
              {showF('electoral_section') && (
              <button type="button" onClick={toggleOtherCity} className={clsx('text-xs font-medium transition-colors', otherCity ? 'text-brand-500' : 'text-gray-400 hover:text-gray-600')}>
                {otherCity ? 'Usar cidade do endereço' : 'Vota em outra cidade'}
              </button>
              )}
            </div>

            {showF('electoral_section') && otherCity && (
              <div className="mb-3 relative">
                <label className="block text-xs text-gray-500 mb-1">Cidade onde vota</label>
                <input
                  value={selectedElectoralCity ? selectedElectoralCity.name : electoralCitySearch}
                  onChange={e => { setElectoralCitySearch(e.target.value); setSelectedElectoralCity(null); setElectoralCityDropdownOpen(true) }}
                  onFocus={() => setElectoralCityDropdownOpen(true)}
                  placeholder="Buscar cidade..." className={inputClass} />
                {selectedElectoralCity && <button type="button" onClick={() => { setSelectedElectoralCity(null); setElectoralCitySearch(''); setZones([]); setSections([]); setForm(f => ({ ...f, tse_zone_id: '', tse_section_id: '' })) }} className="absolute right-3 top-7 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
                {electoralCityDropdownOpen && !selectedElectoralCity && (
                  <>
                    <div className="fixed inset-0" style={{ zIndex: 100002 }} onClick={() => setElectoralCityDropdownOpen(false)} />
                    <div className="absolute left-0 right-0 top-[calc(100%+2px)] bg-white rounded-xl shadow-lg border border-gray-200 max-h-48 overflow-y-auto" style={{ zIndex: 100003 }}>
                      {electoralCities.filter(c => !electoralCitySearch || c.name.toLowerCase().includes(electoralCitySearch.toLowerCase())).slice(0, 50).map(c => (
                        <button key={c.id} type="button" onClick={() => selectElectoralCity(c)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-900">{c.name}</button>
                      ))}
                      {electoralCities.filter(c => !electoralCitySearch || c.name.toLowerCase().includes(electoralCitySearch.toLowerCase())).length === 0 && (
                        <p className="px-4 py-3 text-sm text-gray-400">Nenhuma cidade encontrada</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="space-y-4">
              {showF('voter_registration') && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Título de eleitor {reqF('voter_registration') && <span className="text-error-500">*</span>}</label>
                <input
                  value={form.voter_registration}
                  onChange={e => { setForm({ ...form, voter_registration: e.target.value.replace(/\D/g, '').slice(0, 12) }); setErrors(er => ({ ...er, voter_registration: '' })) }}
                  placeholder="000000000000"
                  inputMode="numeric"
                  className={clsx(inputClass, form.voter_registration.length === 12 && (isValidTituloEleitor(form.voter_registration) ? 'border-green-400' : 'border-error-400'))}
                />
                {form.voter_registration.length === 12 && (
                  <p className={clsx('text-xs mt-1', isValidTituloEleitor(form.voter_registration) ? 'text-green-600' : 'text-error-500')}>
                    {isValidTituloEleitor(form.voter_registration) ? '✓ Válido' : '✗ Inválido'}
                  </p>
                )}
                {errors.voter_registration && <p className="text-xs text-error-500 mt-1">{errors.voter_registration}</p>}
              </div>
              )}
              {showF('electoral_section') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Zona eleitoral {reqF('electoral_section') && <span className="text-error-500">*</span>}</label>
                  <select value={form.tse_zone_id} onChange={e => { onZoneChange(e.target.value); setErrors(er => ({ ...er, tse_zone_id: '' })) }} className={clsx(inputClass, errors.tse_zone_id && 'border-error-300')} disabled={otherCity ? !selectedElectoralCity : !form.geo_city_id}>
                    <option value="">{(otherCity ? selectedElectoralCity : form.geo_city_id) ? 'Selecione' : otherCity ? 'Selecione a cidade' : 'Preencha o CEP'}</option>
                    {zones.map(z => (
                      <option key={z.id} value={String(z.id)}>Zona {z.nr_zona}</option>
                    ))}
                  </select>
                  {errors.tse_zone_id && <p className="mt-1 text-sm text-error-500">{errors.tse_zone_id}</p>}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Seção {reqF('electoral_section') && <span className="text-error-500">*</span>}</label>
                  <select value={form.tse_section_id} onChange={e => { setForm({ ...form, tse_section_id: e.target.value }); setErrors(er => ({ ...er, tse_section_id: '' })) }} className={clsx(inputClass, errors.tse_section_id && 'border-error-300')} disabled={!form.tse_zone_id}>
                    <option value="">Selecione</option>
                    {sections.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nr_secao}{s.nm_local_votacao ? ` — ${s.nm_local_votacao}` : ''}</option>
                    ))}
                  </select>
                  {errors.tse_section_id && <p className="mt-1 text-sm text-error-500">{errors.tse_section_id}</p>}
                </div>
              </div>
              )}
            </div>
          </div>
          )}

          {/* Observações */}
          {showF('notes') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Observações {reqF('notes') && <span className="text-error-500">*</span>}</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anotações..."
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none resize-none" />
          </div>
          )}

          {/* Cadastro familiar */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setFamilyMode(!familyMode); if (!familyMode && familyMembers.length === 0) addFamilyMember() }}>
              <div className={clsx('w-10 h-6 rounded-full transition-colors relative', familyMode ? 'bg-brand-500' : 'bg-gray-300')}>
                <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-xs transition-transform', familyMode ? 'left-[18px]' : 'left-0.5')} />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Cadastro familiar</span>
                <p className="text-xs text-gray-400">Cadastrar mais pessoas no mesmo endereço</p>
              </div>
            </div>

            {familyMode && (
              <div className="space-y-3 mt-3">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Outros membros da família</p>
                {familyMembers.map((member, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4 relative">
                    <button type="button" onClick={() => removeFamilyMember(i)} className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-error-500 hover:bg-error-50">
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <p className="text-xs font-medium text-gray-500 mb-2">Membro {i + 2}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <input value={member.name} onChange={e => updateFamilyMember(i, 'name', e.target.value)} placeholder="Nome *" className={clsx(inputClass, 'h-10 text-sm')} />
                      <input value={member.phone} onChange={e => updateFamilyMember(i, 'phone', maskPhone(e.target.value))} placeholder="Telefone *" className={clsx(inputClass, 'h-10 text-sm')} />
                      <input value={member.cpf} onChange={e => updateFamilyMember(i, 'cpf', maskCPF(e.target.value))} placeholder="CPF" className={clsx(inputClass, 'h-10 text-sm')} />
                      <input value={member.nickname} onChange={e => updateFamilyMember(i, 'nickname', e.target.value)} placeholder="Apelido" className={clsx(inputClass, 'h-10 text-sm')} />
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      <input value={member.voter_registration} onChange={e => updateFamilyMember(i, 'voter_registration', e.target.value)} placeholder="Título de eleitor" className={clsx(inputClass, 'h-10 text-sm')} />
                      <select value={member.tse_zone_id} onChange={e => onFamilyZoneChange(i, e.target.value)} className={clsx(inputClass, 'h-10 text-sm')} disabled={!form.geo_city_id}>
                        <option value="">{form.geo_city_id ? 'Zona' : 'Preencha o CEP'}</option>
                        {zones.map(z => <option key={z.id} value={String(z.id)}>Zona {z.nr_zona}</option>)}
                      </select>
                      <select value={member.tse_section_id} onChange={e => updateFamilyMember(i, 'tse_section_id', e.target.value)} className={clsx(inputClass, 'h-10 text-sm')} disabled={!member.tse_zone_id}>
                        <option value="">Seção</option>
                        {(familySections[i] || []).map(s => <option key={s.id} value={String(s.id)}>{s.nr_secao}{s.nm_local_votacao ? ` — ${s.nm_local_votacao}` : ''}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addFamilyMember}>+ Adicionar membro</Button>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} loading={saving}>Cadastrar</Button>
      </ModalFooter>
    </Modal>
  )
}
